import React from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ContactRound } from "lucide-react";
import { fmt, STATUS_LABELS } from "@/pages/admin/utils/format";
import { InvoiceCell, AdminSearchBar, PaginationFooter, LoyalBadge } from "@/pages/admin/components/common";

const PurchasesSection = ({
  purchases,
  purchasesInput,
  setPurchasesInput,
  purchasesMeta,
  purchasesStatus,
  setPurchasesStatus,
  purchasesStatusCounts,
  purchasesPage,
  setPurchasesPage,
  invoiceByPurchase,
  openClientProfile,
  tapDiag,
  tapDiagLoading,
  tapPing,
  tapPingLoading,
  runTapDiagnostics,
  runTapPing,
}) => (
  <>
            <div className="card-warm p-4 mb-4 flex flex-col gap-3" data-testid="admin-tap-diagnostics-panel">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-heading text-base text-[#222222]">فحص إعدادات بوابة الدفع (Tap)</div>
                  <div className="text-xs text-[#5E5C58] mt-1">لعرض حالة المفاتيح وآخر محاولات الدفع الفاشلة — بدون كشف القيم السرية.</div>
                </div>
                <Button
                  type="button"
                  onClick={runTapDiagnostics}
                  disabled={tapDiagLoading}
                  data-testid="admin-tap-diagnostics-btn"
                  className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white h-9 px-4 text-sm"
                >
                  {tapDiagLoading ? "جارٍ الفحص..." : "فحص إعدادات Tap"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={runTapPing}
                  disabled={tapPingLoading}
                  data-testid="admin-tap-ping-btn"
                  className="rounded-full border-[#0A4D3C]/40 text-[#0A4D3C] hover:bg-[#F6F4EE] h-9 px-4 text-sm"
                >
                  {tapPingLoading ? "جارٍ الاختبار..." : "اختبار اتصال Tap"}
                </Button>
              </div>
              {tapPing && (
                <div
                  className={
                    "border rounded-xl p-3 text-sm " +
                    (tapPing.ok
                      ? "border-[#2E7D32]/30 bg-[#2E7D32]/5"
                      : "border-[#B33939]/30 bg-[#B33939]/5")
                  }
                  data-testid="admin-tap-ping-result"
                >
                  <div className="flex items-center gap-2">
                    <span className={"font-medium " + (tapPing.ok ? "text-[#2E7D32]" : "text-[#B33939]")}>
                      {tapPing.ok ? "الاتصال سليم ✓" : "فشل اتصال Tap"}
                    </span>
                    {typeof tapPing.status === "number" && (
                      <span dir="ltr" className="font-mono text-xs text-[#5E5C58]">HTTP {tapPing.status}</span>
                    )}
                    {tapPing.stage && (
                      <span dir="ltr" className="font-mono text-xs text-[#5E5C58]">stage={tapPing.stage}</span>
                    )}
                  </div>
                  {tapPing.detail && <div className="text-xs mt-1 text-[#222222]">{tapPing.detail}</div>}
                  {Array.isArray(tapPing.errors) && tapPing.errors.length > 0 && (
                    <ul className="mt-1 text-[11px] font-mono text-[#B33939]" dir="ltr">
                      {tapPing.errors.map((er, i) => <li key={i}>· {er}</li>)}
                    </ul>
                  )}
                </div>
              )}
              {tapDiag && (
                <div
                  className="border border-[#E5E1D8] rounded-xl p-3 bg-[#F6F4EE] text-sm space-y-2"
                  data-testid="admin-tap-diagnostics-result"
                >
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div><span className="text-[#5E5C58]">المفتاح السري موجود:</span> <span className="font-medium">{tapDiag.tap_secret_key_present ? "نعم" : "لا"}</span></div>
                    <div><span className="text-[#5E5C58]">المفتاح العام موجود:</span> <span className="font-medium">{tapDiag.tap_publishable_key_present ? "نعم" : "لا"}</span></div>
                    <div dir="ltr"><span className="text-[#5E5C58]">SK fingerprint:</span> <span className="font-mono">{tapDiag.tap_secret_key_fingerprint || "—"}</span></div>
                    <div dir="ltr"><span className="text-[#5E5C58]">PK fingerprint:</span> <span className="font-mono">{tapDiag.tap_publishable_key_fingerprint || "—"}</span></div>
                    <div><span className="text-[#5E5C58]">وضع مباشر (live):</span> <span className="font-medium">{tapDiag.live_mode ? "نعم" : "لا"}</span></div>
                    <div dir="ltr"><span className="text-[#5E5C58]">API base:</span> <span className="font-mono text-xs">{tapDiag.tap_api_base}</span></div>
                  </div>
                  <div>
                    <div className="text-[#5E5C58] mt-1">آخر محاولات شراء فاشلة:</div>
                    {(!tapDiag.recent_failed_purchases || tapDiag.recent_failed_purchases.length === 0) ? (
                      <div className="text-xs text-[#2E7D32] mt-1">لا توجد محاولات فاشلة حديثًا.</div>
                    ) : (
                      <ul className="mt-1 space-y-1" data-testid="admin-tap-diagnostics-failures">
                        {tapDiag.recent_failed_purchases.map((f) => (
                          <li key={f.id} className="text-xs border-t border-[#E5E1D8] pt-1">
                            <div dir="ltr" className="font-mono text-[11px] text-[#5E5C58]">{f.failed_at || "—"} · {f.product_id} · {Number(f.amount).toLocaleString("ar-SA")} ر.س</div>
                            <div className="text-[#B33939]">{f.failure_reason || "—"}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <div className="text-[#5E5C58] mt-2">آخر محاولات حجز جلسة فاشلة:</div>
                    {(!tapDiag.recent_failed_bookings || tapDiag.recent_failed_bookings.length === 0) ? (
                      <div className="text-xs text-[#2E7D32] mt-1">لا توجد محاولات فاشلة حديثًا.</div>
                    ) : (
                      <ul className="mt-1 space-y-1" data-testid="admin-tap-diagnostics-booking-failures">
                        {tapDiag.recent_failed_bookings.map((f) => (
                          <li key={f.id} className="text-xs border-t border-[#E5E1D8] pt-1">
                            <div dir="ltr" className="font-mono text-[11px] text-[#5E5C58]">{f.failed_at || "—"} · {f.service_id} · {f.slot_iso || "—"} · {Number(f.amount).toLocaleString("ar-SA")} ر.س</div>
                            <div className="text-[#B33939]">{f.failure_reason || "—"}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
            <AdminSearchBar
              value={purchasesInput}
              onChange={setPurchasesInput}
              placeholder="ابحث بالاسم، الإيميل، أو رقم الطلب..."
              testId="admin-purchases-search"
              resultCount={purchasesMeta.total}
            />
            <div
              className="flex flex-wrap gap-2 mb-3"
              data-testid="admin-purchases-status-filters"
              role="tablist"
              aria-label="تصفية المشتريات حسب الحالة"
            >
              {[
                { key: "all", label: "الكل" },
                { key: "paid", label: "المدفوعة" },
                { key: "pending", label: "بانتظار الدفع" },
                { key: "canceled", label: "الملغاة" },
                { key: "failed", label: "فشل الدفع" },
              ].map((f) => {
                const active = purchasesStatus === f.key;
                const count = purchasesStatusCounts[f.key];
                return (
                  <button
                    key={f.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    data-testid={`admin-purchases-filter-${f.key}`}
                    onClick={() => setPurchasesStatus(f.key)}
                    className={
                      "rounded-full px-4 h-8 text-xs font-medium border transition " +
                      (active
                        ? "bg-[#0A4D3C] text-white border-[#0A4D3C]"
                        : "bg-white text-[#222222] border-[#E5E1D8] hover:bg-[#F6F4EE]")
                    }
                  >
                    {f.label}
                    {typeof count === "number" && (
                      <span className={"ms-1 " + (active ? "text-white/80" : "text-[#5E5C58]")}>
                        ({count.toLocaleString("ar-SA")})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="card-warm p-0 overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-purchases-table">
                <thead className="bg-[#F6F4EE] text-[#5E5C58]">
                  <tr>
                    <th className="text-start p-4">العميل</th>
                    <th className="text-start p-4">المنتج</th>
                    <th className="text-start p-4">التاريخ</th>
                    <th className="text-start p-4">المبلغ</th>
                    <th className="text-start p-4">الحالة</th>
                    <th className="text-start p-4">الفاتورة</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-[#5E5C58]" data-testid="admin-purchases-empty">
                        {purchasesStatus !== "all" ? "لا توجد مشتريات بهذه الحالة" : "لا توجد مشتريات."}
                      </td>
                    </tr>
                  )}
                  {purchases.map((p) => (
                    <tr key={p.id} className="border-t border-[#E5E1D8]">
                      <td className="p-4">
                        <div className="font-medium flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            data-testid={`admin-purchases-open-client-${p.id}`}
                            onClick={() => openClientProfile(p)}
                            title="فتح ملف العميل"
                            className="inline-flex items-center gap-1 text-[#0A4D3C] hover:underline focus:outline-none focus:ring-1 focus:ring-[#0A4D3C] rounded"
                          >
                            <ContactRound className="w-3.5 h-3.5" />
                            <span>{p.user_name || "—"}</span>
                          </button>
                          {p.user_is_loyal && <LoyalBadge count={p.user_transaction_count} />}
                        </div>
                        <div className="text-xs text-[#5E5C58]" dir="ltr">{p.user_email}</div>
                      </td>
                      <td className="p-4">{p.product_title_ar}</td>
                      <td className="p-4">{fmt(p.created_at)}</td>
                      <td className="p-4">{Number(p.amount).toLocaleString("ar-SA")} ر.س</td>
                      <td className="p-4">
                        <Badge className="rounded-full bg-stone-100 text-stone-700">
                          {STATUS_LABELS[p.status] || p.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <InvoiceCell invoice={invoiceByPurchase[p.id]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationFooter
              page={purchasesPage}
              totalPages={purchasesMeta.total_pages}
              total={purchasesMeta.total}
              onPrev={() => setPurchasesPage((p) => Math.max(1, p - 1))}
              onNext={() => setPurchasesPage((p) => Math.min(purchasesMeta.total_pages || 1, p + 1))}
              testId="admin-purchases-pager"
            />
  </>
);

export { PurchasesSection };
